import React, { useState, useEffect } from 'react';
import { fetchCinemaDetail, fetchCinemaScreenings } from '../api/client';
import { useAuth } from '../context/AuthContext';
import DateRangePicker from '../components/DateRangePicker';
import {
  ComparedTo, DailyBars, KpiRow, MoreButton, OccupancyBars, Tabs, VenueTable, col, card, cardTitle,
} from '../components/VenueCharts';
import { useScreeningModals, LANGUAGE_LABELS } from '../components/ScreeningModals';
import { TEXT_FAINT } from '../theme';

/**
 * One branch over a date range. Four figures, one chart, what does well here,
 * its top films, and its screenings. Layout rules: CLAUDE.md §0.
 */

const fmt = (v) => (v || 0).toLocaleString();

const DIMENSIONS = [
  { key: 'genre', label: 'ז׳אנר' },
  { key: 'language', label: 'שפה' },
  { key: 'daypart', label: 'שעה' },
  { key: 'weekday', label: 'יום' },
];

const PAGE = 20;

const dayLabel = (iso) => {
  const d = new Date(iso);
  return `${d.toLocaleDateString('he-IL', { weekday: 'long' })} ${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`;
};

function ScreeningRow({ r, past, onMovieClick, onSnapshot, onNotScanned }) {
  const { hasPermission } = useAuth();
  const d = new Date(r.showtime);
  const version = [r.format, r.language ? (LANGUAGE_LABELS[r.language] || r.language) : null]
    .filter(Boolean).join(' · ');
  const badge = { padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700, border: 'none' };
  let status = null;
  if (past && r.status === 'cancelled') {
    status = <span style={{ ...badge, background: 'var(--surf-alt)', color: 'var(--faint)' }}>בוטלה</span>;
  } else if (past && r.final_scraped_at && hasPermission('movie_detail.snapshot_popup')) {
    status = <button onClick={(e) => { e.stopPropagation(); onSnapshot(r); }}
                     style={{ ...badge, cursor: 'pointer', background: 'var(--ok-bg)', color: 'var(--ok)' }}>נסגר</button>;
  } else if (past && !r.final_scraped_at && r.status === 'closed' && hasPermission('movie_detail.unscan_popup')) {
    status = <button onClick={(e) => { e.stopPropagation(); onNotScanned(r); }}
                     style={{ ...badge, cursor: 'pointer', background: 'var(--danger-bg)', color: 'var(--danger)' }}>לא נסרק</button>;
  }
  return (
    <div onClick={() => onMovieClick && onMovieClick(r.movie_id)}
         style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 2px',
                  borderTop: '1px solid var(--brd)', cursor: 'pointer' }}>
      <div style={{ width: 48, flexShrink: 0, textAlign: 'center', fontSize: 15, fontWeight: 700,
                    color: 'var(--txt)', fontVariantNumeric: 'tabular-nums' }}>
        {d.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--txt)', overflow: 'hidden',
                      textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.title}</div>
        <div style={{ fontSize: 11, color: 'var(--faint)', overflow: 'hidden', textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap' }}>{[r.hall, version].filter(Boolean).join(' · ')}</div>
      </div>
      <div style={{ flexShrink: 0, textAlign: 'left', minWidth: 60 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--txt)', fontVariantNumeric: 'tabular-nums' }}>
          {fmt(r.tickets_sold)}{r.total_seats ? <span style={{ color: 'var(--faint)', fontWeight: 400 }}>/{r.total_seats}</span> : null}
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'flex-end' }}>
          {status}
          {r.total_seats ? <span style={{ fontSize: 11, color: 'var(--muted)' }}>{r.occupancy}%</span> : null}
        </div>
      </div>
    </div>
  );
}

function Screenings({ cinemaId, range, onMovieClick }) {
  const [when, setWhen] = useState('upcoming');
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const { openSnapshot, openNotScanned, modals } = useScreeningModals();

  useEffect(() => {
    setRows(null);
    fetchCinemaScreenings(cinemaId, when, range.start, range.end, 0, PAGE)
      .then(d => { setRows(d.rows); setTotal(d.total); })
      .catch(() => { setRows([]); setTotal(0); });
  }, [cinemaId, when, range.start, range.end]);

  const more = () => fetchCinemaScreenings(cinemaId, when, range.start, range.end, rows.length, PAGE)
    .then(d => setRows(prev => [...prev, ...d.rows]));

  return (
    <div style={card}>
      {modals}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
                    flexWrap: 'wrap', marginBottom: 6 }}>
        <div style={{ ...cardTitle, marginBottom: 0 }}>הקרנות</div>
        <Tabs value={when} onChange={setWhen} options={[
          { key: 'upcoming', label: 'הבאות' }, { key: 'past', label: 'שעברו' },
        ]} />
      </div>
      {rows === null && <div style={{ padding: 24, textAlign: 'center', color: TEXT_FAINT }}>טוען...</div>}
      {rows && rows.length === 0 && (
        <div style={{ padding: 24, textAlign: 'center', color: TEXT_FAINT }}>אין הקרנות</div>
      )}
      {rows && rows.map((r, i) => {
        const day = dayLabel(r.showtime);
        const newDay = i === 0 || dayLabel(rows[i - 1].showtime) !== day;
        return (
          <React.Fragment key={r.id}>
            {newDay && (
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', padding: '12px 2px 2px' }}>{day}</div>
            )}
            <ScreeningRow r={r} past={when === 'past'} onMovieClick={onMovieClick}
                          onSnapshot={openSnapshot} onNotScanned={openNotScanned} />
          </React.Fragment>
        );
      })}
      {rows && rows.length < total && <MoreButton onClick={more}>הצג עוד</MoreButton>}
    </div>
  );
}

function CinemaDetail({ cinemaId, range, onRangeChange, onBack, onMovieClick, refreshNonce }) {
  const [dim, setDim] = useState('genre');
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    fetchCinemaDetail(cinemaId, range.start, range.end)
      .then(setData).catch(() => setData({ error: 'טעינת הסניף נכשלה' }));
  }, [cinemaId, range.start, range.end, refreshNonce]);

  const c = data && data.cinema;
  const s = (data && data.summary) || {};

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={onBack} aria-label="חזרה" style={{
            background: 'var(--surf)', border: '1px solid var(--brd)', borderRadius: 10, width: 36, height: 36,
            cursor: 'pointer', color: 'var(--txt)', fontSize: 16, flexShrink: 0,
          }}>→</button>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--txt)' }}>{c ? c.name_he : ''}</div>
            {c && <div style={{ fontSize: 12, color: 'var(--muted)' }}>{c.chain_he} · {c.city_he}</div>}
          </div>
        </div>
        <div>
          <DateRangePicker start={range.start} end={range.end} mode={range.mode} onChange={onRangeChange} />
          {data && !data.error && <ComparedTo range={data.range} />}
        </div>
      </div>

      {!data && <div style={{ textAlign: 'center', padding: 60, color: TEXT_FAINT }}>טוען...</div>}
      {data && data.error && <div style={{ ...card, color: 'var(--danger)' }}>{data.error}</div>}

      {data && !data.error && (
        <>
          <KpiRow items={[
            { label: 'כרטיסים', value: fmt(s.tickets), change: s.change },
            { label: 'הקרנות', value: fmt(s.screenings) },
            { label: 'תפוסה', value: `${s.occupancy}%` },
            { label: 'מקום', value: s.rank ? `${s.rank}` : '—', sub: s.ranked ? `מתוך ${s.ranked}` : null },
          ]} />

          <div style={card}>
            <div style={cardTitle}>כרטיסים לפי יום</div>
            <DailyBars rows={data.daily} />
          </div>

          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
                          flexWrap: 'wrap', marginBottom: 12 }}>
              <div style={{ ...cardTitle, marginBottom: 0 }}>מה מצליח כאן</div>
              <Tabs value={dim} onChange={setDim} options={DIMENSIONS} />
            </div>
            <OccupancyBars rows={data.mix[dim] || []} />
          </div>

          <VenueTable title="סרטים מובילים" rows={data.top_movies} pageSize={5}
                      columns={[col.name('סרט', r => r.title_he, null, true),
                                col.tickets, col.screenings, col.occupancy]}
                      onClick={r => onMovieClick && onMovieClick(r.movie_id)} />

          <Screenings cinemaId={cinemaId} range={range} onMovieClick={onMovieClick} />
        </>
      )}
    </div>
  );
}

export default CinemaDetail;
